import { ChangeEvent, useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import {
	DateInput,
	FieldLayout,
	Fieldset,
	FormField,
	Input,
	Label,
	Legend,
} from '@anwb/poncho/components/forms';
import Panel from '@anwb/poncho/components/panel';
import { useTransformVisualValue } from '@anwb/poncho/utilities/form-helpers';
import { formatEmail, formatTelephone } from '@anwb/poncho/utilities/formatters';
import emailRegexValidation from '@anwb/poncho/validations/validations-email';
import { regex as telephoneRegexValidation } from '@anwb/poncho/validations/validations-telephone';
import { useGaFunnelInfo } from 'hooks/useGaFunnelInfo';
import { setCustomerExtraInformation } from 'store/actions';
import { plCustomerSelector } from 'store/selectors';
import { PLCustomerExtraInformation } from 'types/plCustomerData';

import { getMaxBirthdate, handleFormGaEvent, validateBirthdate } from '../utils';

export const MissingPersonalDataForm = ({ formHook }: { formHook: UseFormReturn }) => {
	const { register, setValue, formState, trigger } = formHook;

	const { email, phoneNumber, birthdate, extraInformation } = useSelector(plCustomerSelector);
	const dispatch = useDispatch();

	const [, transformedPhoneNumber, handlePhoneNumberChange] = useTransformVisualValue('', [
		formatTelephone,
	]);
	const [, transformedEmail, handleEmailChange] = useTransformVisualValue('', [formatEmail]);
	const gaFunnelInfo = useGaFunnelInfo();

	useEffect(() => {
		if (extraInformation) {
			Object.keys(extraInformation).forEach((key) => {
				setValue(key, extraInformation[key as keyof PLCustomerExtraInformation]);
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (email && phoneNumber && birthdate) {
		return null;
	}

	return (
		<Panel hasOuterSpacing={false}>
			<Panel.Content>
				<Fieldset legend={<Legend>Contactgegevens</Legend>}>
					<FieldLayout>
						{!phoneNumber && (
							<FormField
								size="l"
								label={<Label text="Telefoonnummer" reference="phoneNumber" required />}
							>
								<Input
									reference="phoneNumber"
									placeholder="Bijv.: 06 12345678"
									{...register('phoneNumber', {
										required: 'Je hebt je telefoonnummer nog niet ingevuld.',
										pattern: {
											value: new RegExp(telephoneRegexValidation),
											message: 'Je hebt je telefoonnummer onjuist ingevuld.',
										},
									})}
									onChange={handlePhoneNumberChange}
									onBlur={(event) => {
										setValue('phoneNumber', transformedPhoneNumber);
										trigger('phoneNumber');
										dispatch(
											setCustomerExtraInformation({
												phoneNumber: event.target.value,
												email: extraInformation.email,
												birthdate: extraInformation.birthdate,
											})
										);
										handleFormGaEvent('phoneNumber', formState, event, gaFunnelInfo);
									}}
								/>

								{formState.errors.phoneNumber && (
									<FormField.Error aria-live="assertive">
										{formState.errors.phoneNumber.message as string}
									</FormField.Error>
								)}
							</FormField>
						)}

						{!email && (
							<FormField size="l" label={<Label text="E-mailadres" reference="email" required />}>
								<Input
									reference="email"
									placeholder="Bijv.: voorbeeld@domein.nl"
									{...register('email', {
										required: 'Je hebt je e-mailadres nog niet ingevuld.',
										pattern: {
											value: new RegExp(emailRegexValidation),
											message: 'Je hebt je e-mailadres onjuist ingevuld.',
										},
									})}
									onChange={handleEmailChange}
									onBlur={(event) => {
										setValue('email', transformedEmail);
										trigger('email');
										dispatch(
											setCustomerExtraInformation({
												phoneNumber: extraInformation.phoneNumber,
												email: transformedEmail,
												birthdate: extraInformation.birthdate,
											})
										);
										handleFormGaEvent('email', formState, event, gaFunnelInfo);
									}}
								/>

								{formState.errors.email && (
									<FormField.Error aria-live="assertive">
										{formState.errors.email.message as string}
									</FormField.Error>
								)}
							</FormField>
						)}

						{!birthdate && (
							<FormField
								size="l"
								label={<Label text="Geboortedatum" reference="birthdate" required />}
							>
								<DateInput
									reference="birthdate"
									placeholder="dd-mm-jjjj"
									min="1900-01-01"
									max={getMaxBirthdate()}
									{...register('birthdate', {
										required: 'Je hebt je geboortedatum nog niet ingevuld.',
										validate: validateBirthdate,
									})}
									onChange={(event: ChangeEvent<HTMLInputElement>) => {
										setValue('birthdate', event.target.value);
										trigger('birthdate');
										dispatch(
											setCustomerExtraInformation({
												phoneNumber: extraInformation.phoneNumber,
												email: extraInformation.email,
												birthdate: event.target.value,
											})
										);
										handleFormGaEvent('birthdate', formState, event, gaFunnelInfo);
									}}
									value={extraInformation?.birthdate || ''}
								/>

								{formState.errors.birthdate && (
									<FormField.Error aria-live="assertive">
										{formState.errors.birthdate.message as string}
									</FormField.Error>
								)}
							</FormField>
						)}
					</FieldLayout>
				</Fieldset>
			</Panel.Content>
		</Panel>
	);
};
